import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import ProductPlaceholder from "@/components/ProductPlaceholder";
import BuyButton from "@/components/BuyButton";
import { PRICES, SENIORS_DECK_IMAGE } from "@/data/content";

const ShopDeck = () => {
  return (
    <div data-testid="shop-deck-page" className="bg-cream">
      <Seo
        title="Individual UNSTUCK Seniors Deck — 54 Senior Activity Cards"
        description="One 54-card deck of simple 15-minute activity cards for older adults. Use solo, with a caregiver, or in groups. A thoughtful gift for seniors, parents, and grandparents."
        path="/shop/seniors-deck"
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
          <div className="grid gap-6">
            <ProductPlaceholder label="Individual UNSTUCK Seniors deck" ratio="aspect-[4/3]" testId="shop-deck-placeholder" src={SENIORS_DECK_IMAGE} alt="Individual UNSTUCK Seniors deck box with activity cards" />
            <ProductPlaceholder label="Card close-up photos" ratio="aspect-[16/9]" testId="shop-deck-closeup-placeholder" src={SENIORS_DECK_IMAGE} alt="UNSTUCK Seniors activity cards" />
          </div>
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">
              Shop individual deck
            </p>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-tight text-ink mb-4">
              Individual UNSTUCK Seniors Deck
            </h1>
            <p className="font-heading font-black text-4xl text-sage mb-8" data-testid="shop-deck-price">
              ${PRICES.seniorsDeck.toFixed(2)} <span className="text-lg font-body font-medium text-ink/60">+ shipping</span>
            </p>
            <p className="text-lg leading-relaxed text-ink/80 mb-6">
              One 54-card deck of simple 15-minute activities designed to help older adults take one small
              step, stay engaged, connect, participate, and enjoy meaningful moments throughout the day.
            </p>
            <ul className="space-y-3 mb-10">
              {[
                "54 simple activity cards in one deck",
                "Use it solo, with a caregiver or family member, partner-to-partner, or in a group",
                "PULL IT • PASS IT • TRADE IT • EARN IT — 4 ways to play",
                "A thoughtful gift for a senior, parent, or grandparent",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-base text-ink/85">
                  <Check className="h-5 w-5 text-sage shrink-0 mt-0.5" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <BuyButton
              lookupKey="seniors_deck"
              testId="shop-deck-buy-button"
              className="w-full sm:w-auto bg-sage text-white hover:bg-sage-dark shadow-[0_2px_10px_rgba(74,93,78,0.2)]"
            >
              Buy the Individual Deck — ${PRICES.seniorsDeck.toFixed(2)}
            </BuyButton>
            <p className="mt-4 text-base text-ink/70" data-testid="shop-deck-shipping-note">
              Ships within 3–5 business days. Flat $12.00 shipping per order.
            </p>
            <div className="mt-10 bg-white rounded-2xl border border-border p-6">
              <p className="text-base leading-relaxed text-ink/80 mb-4">
                Looking for the complete set? The{" "}
                <span className="font-semibold text-ink">Complete UNSTUCK Seniors Program</span> includes
                four individually packaged decks, printed instructions, UNSTUCK Bucks, printable PDF
                support materials, and program packaging for ${PRICES.seniorsProgram.toFixed(0)} + shipping.
              </p>
              <Link
                to="/seniors#program"
                data-testid="shop-deck-full-program-link"
                className="inline-flex items-center gap-2 text-sage font-semibold hover:text-sage-dark transition-colors"
              >
                See the Full Seniors Program <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopDeck;
